/**
 * useParentSessions hook
 * React Query hook for fetching the parent's children's session history including
 * per-session highlights (strengths/improvements) from stored report summaries.
 *
 * Implements US-010 (T5): useParentSessions hook.
 * Modelled directly on useParentDashboard (frontend/src/hooks/useParentDashboard.ts).
 * Calls the EXISTING GET /parent/sessions endpoint (TS-017) via getParentSessions
 * (frontend/src/services/parent.ts, US-010 T5).
 *
 * Cache key: ['parent', 'sessions'] — separate from ['parent', 'dashboard'] so
 * switching tabs does not refire either query (0 extra API calls on tab toggle).
 * staleTime: 30s — consistent with useParentDashboard convention.
 *
 * TO-LL-036: errors are never swallowed — React Query error object surfaces to callers
 *   so Reports tab can render ErrorState with a retry button.
 * TO-LL-044: returns ParentSession[] with exact camelCase keys from the API contract.
 */

import { useQuery } from '@tanstack/react-query';
import { getParentSessions } from '@/services/parent';
import type { ParentSession, ParentSessionsResponse } from '@/types/coins';

/**
 * Return type for useParentSessions hook.
 * Modelled on UseParentDashboardReturn for consistency.
 */
export interface UseParentSessionsReturn {
  /**
   * List of parent-scoped session summaries, most-recent first.
   * Empty array during loading or when the parent has no sessions yet.
   * TO-LL-044: each entry has exact camelCase keys (sessionId, childDisplayName,
   *   passageTitle, date, questionCount, starRating, firstTryCorrect, firstTryTotal,
   *   status, strengths, improvements).
   */
  sessions: ParentSession[];
  /** True during initial fetch */
  isLoading: boolean;
  /** Error object if fetch failed; null on success. Never swallowed (TO-LL-036). */
  error: Error | null;
  /** Call to manually refetch — wired to ErrorState's onRetry in the Reports tab */
  refetch: () => void;
}

/**
 * Hook for fetching and managing parent sessions data.
 * Uses React Query with 30s staleTime — consistent with useParentDashboard.
 * Refetch-on-window-focus is sufficient; no polling needed for session history.
 *
 * Implements US-010 (T5).
 */
export function useParentSessions(): UseParentSessionsReturn {
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useQuery<ParentSessionsResponse, Error>({
    queryKey: ['parent', 'sessions'],
    queryFn: () => getParentSessions(),
    staleTime: 30000, // 30 seconds — consistent with useParentDashboard
  });

  // Default to [] during loading so callers never handle undefined.
  const sessions: ParentSession[] = data?.sessions ?? [];

  return {
    sessions,
    isLoading,
    error,
    refetch: () => {
      refetch();
    },
  };
}
